import i18next from "i18next";
import { useCallback, useState } from "react";

import { IApiError } from "../../../@types/ApiError";
import {
  fileTransferService,
  IFileDownloadRequest,
  IFileUploadRequest,
} from "../../../services/admin";
import {
  extractErrors,
  getSummaryErrorMessage,
} from "../../../utils/apiErrorUtils";
import { downloadFromResponse } from "../../../utils/fileDownload";
import { useToast } from "../../useToast";

interface IDownloadOptions {
  /** Saved-as name when the server sends no Content-Disposition filename. */
  fallbackFilename: string;
  /** Shown on success; omit for a silent download. */
  successMessage?: string;
  /** Overrides the generic failure text (server messages still win). */
  errorMessage?: string;
}

/**
 * Downloads a file from an endpoint and saves it, with a loading flag and an
 * error toast (EN-5148). Every Retool export button migrates onto this:
 *
 *   const { downloadFile, isDownloading } = useFileDownload();
 *   await downloadFile(
 *     { url: `/Retool/OneMsts/${id}/Export` },
 *     { fallbackFilename: `1MSTS-${id}.xlsx` }
 *   );
 *
 * Returns true when a file was actually saved. A failed request writes
 * nothing to disk — `downloadFromResponse` throws on non-2xx rather than
 * saving the error body (the shared axios interceptor resolves those
 * responses as if they had succeeded).
 */
export function useFileDownload() {
  const { showToast } = useToast();
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadFile = useCallback(
    async (
      request: IFileDownloadRequest,
      { fallbackFilename, successMessage, errorMessage }: IDownloadOptions
    ): Promise<boolean> => {
      setIsDownloading(true);
      try {
        const response = await fileTransferService.download(request);
        await downloadFromResponse(response, { fallbackFilename });

        if (successMessage) {
          showToast(successMessage, "success");
        }
        return true;
      } catch (error) {
        const serverMessage =
          error instanceof Error && error.message ? error.message : undefined;
        showToast(
          errorMessage ?? serverMessage ?? i18next.t("common_downloadFailed"),
          "error"
        );
        return false;
      } finally {
        setIsDownloading(false);
      }
    },
    [showToast]
  );

  return { downloadFile, isDownloading };
}

export type IUploadResult<T> =
  | { ok: true; data: T }
  | { ok: false; status: number; message: string; errors: IApiError[] };

interface IUploadOptions {
  successMessage?: string;
  errorMessage?: string;
  /** Set false to render the failure inline instead of toasting it. */
  showErrorToast?: boolean;
}

/**
 * Uploads a multipart body with progress and server-error reporting
 * (EN-5148) — the tenant logo (`PUT /Retool/Tenants/Logo/{id}`) and the
 * patient bulk import (`POST /Retool/Patients/BulkImports`) both run through
 * this.
 *
 * The result is returned rather than thrown so a form can show field-level
 * messages: `errors` carries the API's `ErrorResponse.errors` entries, each
 * with the `propertyName` that failed. Validate the file with `validateFile`
 * before calling — this covers what only the server can know.
 */
export function useFileUpload<T = unknown>() {
  const { showToast } = useToast();
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const uploadFile = useCallback(
    async (
      request: IFileUploadRequest,
      {
        successMessage,
        errorMessage,
        showErrorToast = true,
      }: IUploadOptions = {}
    ): Promise<IUploadResult<T>> => {
      setIsUploading(true);
      setProgress(0);

      const fail = (
        status: number,
        message: string,
        errors: IApiError[] = []
      ): IUploadResult<T> => {
        if (showErrorToast) {
          showToast(errorMessage ?? message, "error");
        }
        return { ok: false, status, message, errors };
      };

      try {
        const response = await fileTransferService.upload<T>({
          ...request,
          onUploadProgress: (percent) => {
            setProgress(percent);
            request.onUploadProgress?.(percent);
          },
        });

        // No response at all (network/CORS): the interceptor resolves the
        // rejection with `error.response`, which is undefined there.
        if (!response) {
          return fail(0, i18next.t("common_noServerResponse"));
        }

        const status = response.status ?? 0;
        if (status < 200 || status >= 300) {
          const errors = extractErrors(response);
          return fail(status, getSummaryErrorMessage(errors), errors);
        }

        if (successMessage) {
          showToast(successMessage, "success");
        }
        return { ok: true, data: response.data };
      } catch {
        return fail(0, i18next.t("common_uploadFailed"));
      } finally {
        setIsUploading(false);
      }
    },
    [showToast]
  );

  return { uploadFile, isUploading, progress };
}
